import { FC, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import { ProjectionProviderType } from "@app/projects/enumerators";
import { ProjectionProviderDetails } from "@app/projects/projection-provider";
import projectionProviderDetailsReducer, { projectionProviderDetailsInitialState } from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { WrappedTerminateProvidersPanel as TerminateProvidersPanel} from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { ProviderActionsFlyoutEnum } from "@app/projects/network-assessment/provider-actions/enumerators";
import { ProviderActionsFlyoutProps } from "@app/projects/network-assessment/provider-actions/interfaces";
import { ProviderActionsDrawerContext } from "@app/projects/network-assessment/provider-actions/contexts/ProviderActionsDrawerContext";
import { FlyoutContext } from "@app/shared/Flyouts";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";

const ProviderActionsFlyout: FC<ProviderActionsFlyoutProps> = (props: ProviderActionsFlyoutProps) => {
  const { type } = props;
  const actionsContext = useContext(ProviderActionsDrawerContext);
  const flyoutContext = useContext(FlyoutContext);

  const [projectionProvidersState, projectionProvidersReducer] = useReducer(
    projectionProviderDetailsReducer,
    projectionProviderDetailsInitialState
  );

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    flyoutContext.handleCloseFlyout();
  }, [actionsContext.overlayClickToken]);

  useEffect(() => {
    if (type !== ProviderActionsFlyoutEnum.Termination) {
      props.onDirtyStateChange?.(projectionProvidersState.hasChanged);
    }
  }, [projectionProvidersState.hasChanged, props.onDirtyStateChange]);

  const handleUpdate = useCallback(() => {
    actionsContext.requestRulesRefresh();
  }, [actionsContext]);

  const { processAll } = actionsContext;

  const handleProjectionUpdate = useCallback((runProjection: boolean) => {
    if (runProjection) {
      actionsContext.processAll();
    }
    else {
      handleUpdate();
    }
  }, [processAll, handleUpdate]);

  return (
    <div>
      {type === ProviderActionsFlyoutEnum.NetNew && (
        <ProjectionProviderDetails
          accessPoints={[]}
          crossSpecialtyAndNetworkType={props.projectionProvider?.projectedByNPI2 ? props.projectionProvider : null}
          formDispatch={projectionProvidersReducer}
          formState={projectionProvidersState}
          hasProvidersWithoutNPI={false}
          isRunning={false}
          isZipProject={props.isZipProject}
          networkAssessmentAnalysisType={null}
          onProjectionProviderUpdated={handleProjectionUpdate}
          projectId={actionsContext.projectId}
          projectionPanelMode={props.projectionPanelMode}
          projectionProvider={props.projectionProvider}
          projectionProviderType={ProjectionProviderType.Target}
          providerNetworkType={ProviderNetworkTypeEnum.NetNew}
          showAffiliationsInsights={false}
          showHistoryInViewMode={true}
          specialtyGroupCode={props.rule.specialtyCode}
        />
      )}

      {type === ProviderActionsFlyoutEnum.Projection && (
        <ProjectionProviderDetails
          accessPoints={[]}
          crossSpecialtyAndNetworkType={props.projectionProvider?.projectedByNPI2 || props.projectionProvider?.projectedByCCN ? props.projectionProvider : null}
          formDispatch={projectionProvidersReducer}
          formState={projectionProvidersState}
          hasProvidersWithoutNPI={false}
          isRunning={false}
          isZipProject={props.isZipProject}
          networkAssessmentAnalysisType={null}
          onProjectionProviderUpdated={handleProjectionUpdate}
          projectId={actionsContext.projectId}
          projectionPanelMode={props.projectionPanelMode}
          projectionProvider={props.projectionProvider}
          projectionProviderType={props.isTarget ? ProjectionProviderType.Target : ProjectionProviderType.ExcludeImpact}
          providerNetworkType={null}
          showAffiliationsInsights={false}
          showHistoryInViewMode={true}
          specialtyGroupCode={props.rule.specialtyCode}
        />
      )}

      {type === ProviderActionsFlyoutEnum.Termination && (
        <TerminateProvidersPanel
          npis={props.npis}
          onSubmit={handleUpdate}
          crossSpecialtyAndNetwork={props.termination?.terminateByNPI2 || props.termination?.terminateByCCN ? { allLocations: true, allProjects: false, allSpecialties: true} : null}
          projectId={actionsContext.projectId}
          selectedProviders={[]}
          setHasUnsavedChangesInParent={props.onDirtyStateChange}
          showAffiliationsInsights={false}
          showNetworkIntegrityReason={actionsContext.showNetworkIntegrityReason}
          terminatePanelMode={props.terminatePanelMode}
          termination={props.termination}
        />
      )}
    </div>
  );
};

export default ProviderActionsFlyout;